import axios from "axios";
import BASE_URL from "../../config";

// Get the auth token from localStorage and return headers
const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    Authorization: `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

// Get next receipt number
export const getNextReceiptNumber = async (businessId, type) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-next-receipt-number`,
      { BusinessId: businessId, Type: type },
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting next receipt number:", error);
    throw error;
  }
};

// Get customer credit invoices
// BusinessId is optional - if null, returns invoices across all businesses
// includePaidInvoices: if true, returns all invoices including fully paid ones (for Payment History). If false, only unpaid invoices (default for Receipt page)
export const getCustomerCreditInvoices = async (customerId, businessId = null, branchId = null, includePaidInvoices = false) => {
  console.log('🌐 [API] getCustomerCreditInvoices called with:', {
    customerId,
    businessId,
    branchId,
    includePaidInvoices,
    customerIdType: typeof customerId,
    businessIdType: typeof businessId,
    branchIdType: typeof branchId,
    url: `${BASE_URL}/api/Receipt/get-customer-credit-invoices`
  });
  
  try {
    const payload = {
      CustomerId: customerId,
      IncludePaidInvoices: includePaidInvoices
    };
    
    // Only include BusinessId and BranchId if provided
    if (businessId !== null && businessId !== undefined) {
      payload.BusinessId = businessId;
    }
    if (branchId !== null && branchId !== undefined) {
      payload.BranchId = branchId;
    }
    
    console.log('📤 [API] Request payload:', payload);
    console.log('📤 [API] Request headers:', getAuthHeader());
    
    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-customer-credit-invoices`,
      payload,
      { headers: getAuthHeader() }
    );
    
    console.log('📥 [API] Response received:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      dataLength: Array.isArray(response.data) ? response.data.length : 'N/A'
    });
    
    return response.data;
  } catch (error) {
    console.error("❌ [API] Error getting customer credit invoices:", error);
    console.error("❌ [API] Error details:", {
      message: error?.message,
      response: error?.response,
      responseData: error?.response?.data,
      responseStatus: error?.response?.status,
      responseStatusText: error?.response?.statusText,
      request: error?.request,
      config: error?.config,
      url: error?.config?.url,
      method: error?.config?.method,
      payload: error?.config?.data
    });
    throw error;
  }
};

// Save receipt
export const saveReceipt = async (receiptData) => {
  try {
    const headers = getAuthHeader();
    
    // If receiptData is FormData (for file upload), don't set Content-Type
    if (receiptData instanceof FormData) {
      const response = await axios.post(
        `${BASE_URL}/api/Receipt/save-receipt`,
        receiptData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } else {
      // For regular JSON data
      const response = await axios.post(
        `${BASE_URL}/api/Receipt/save-receipt`,
        receiptData,
        {
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    }
  } catch (error) {
    console.error("Error saving receipt:", error);
    throw error;
  }
};

// Get receipts with filters and pagination
export const getReceipts = async (filter = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-receipts`,
      filter,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting receipts:", error);
    throw error;
  }
};

// Get receipt by ID
export const getReceiptById = async (receiptId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/Receipt/get-receipt-by-id/${receiptId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting receipt by ID:", error);
    throw error;
  }
};

// API to export customer receipts summary to Excel
export const exportCustomerReceiptsSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-customer-receipts-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CustomerReceiptsSummary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting customer receipts summary:", error);
    throw error;
  }
};

// API to export customer receipts details to Excel
export const exportCustomerReceiptsDetailsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-customer-receipts-details-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CustomerReceiptsDetails_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting customer receipts details:", error);
    throw error;
  }
};

// API to export cash deposits summary to Excel
export const exportCashDepositsSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-cash-deposits-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CashDepositsSummary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting cash deposits summary:", error);
    throw error;
  }
};

// API to export cash deposits details to Excel
export const exportCashDepositsDetailsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-cash-deposits-details-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CashDepositsDetails_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting cash deposits details:", error);
    throw error;
  }
};
